import { Injectable } from "@angular/core";
import { BehaviorSubject, lastValueFrom } from "rxjs";
import {
  CustomerModel,
  CustomerService,
  ItemModel,
  ItemService,
  UserResponseModel,
  UserService,
} from "../swagger";

@Injectable()
export class DataService {
  allCustomers = new BehaviorSubject<CustomerModel[]>([]);
  itemsList = new BehaviorSubject<ItemModel[]>([]);
  allUsersList = new BehaviorSubject<UserResponseModel[]>([]);
  // True until the first items fetch has completed (success or failure) so the
  // UI can show a loading state instead of an empty "No result found" list.
  itemsLoaded = new BehaviorSubject<boolean>(false);
  // Data-only items list (/api/item/AllItems) used to populate filter
  // dropdowns. It is far cheaper than /api/item/all, is kept in memory for the
  // whole session and is refreshed by the side-menu refresh button.
  dropdownItemsList = new BehaviorSubject<ItemModel[]>([]);
  dropdownItemsLoaded = new BehaviorSubject<boolean>(false);
  private _loadingCustomers: Promise<void> | null = null;
  private _loadingItems: Promise<void> | null = null;
  private _loadingDropdownItems: Promise<void> | null = null;

  constructor(
    private customerService: CustomerService,
    private userService: UserService,
    private itemService: ItemService
  ) {}

  async loadAllCustomers() {
    if (this._loadingCustomers) {
      return this._loadingCustomers;
    }
    this._loadingCustomers = this._doLoadAllCustomers();
    try {
      await this._loadingCustomers;
    } finally {
      this._loadingCustomers = null;
    }
  }

  private async _doLoadAllCustomers() {
    try {
      let result: any = await lastValueFrom(
        this.customerService.apiCustomerAllGet({})
      );
      this.allCustomers.next(result.data as any);
    } catch (e) {
      this.allCustomers.next([]);
      throw e;
    }
  }

  async getAllItemsList() {
    // De-dupe concurrent callers (app startup + page enter) so we only fetch
    // once and share the same in-flight request.
    if (this._loadingItems) {
      return this._loadingItems;
    }
    this._loadingItems = this._doGetAllItemsList();
    try {
      await this._loadingItems;
    } finally {
      this._loadingItems = null;
    }
  }

  private async _doGetAllItemsList() {
    try {
      let result = (await lastValueFrom(
        this.itemService.apiItemAllGet({})
      )) as any;
      this.itemsList.next(result.data ? result.data : []);
    } catch (e) {
      this.itemsList.next([]);
      throw e;
    } finally {
      this.itemsLoaded.next(true);
    }
  }

  async getDropdownItemsList() {
    // De-dupe concurrent callers (app startup + page enter) so we only fetch
    // once and share the same in-flight request.
    if (this._loadingDropdownItems) {
      return this._loadingDropdownItems;
    }
    this._loadingDropdownItems = this._doGetDropdownItemsList();
    try {
      await this._loadingDropdownItems;
    } finally {
      this._loadingDropdownItems = null;
    }
  }

  private async _doGetDropdownItemsList() {
    // Flip back to "not loaded" so a refresh re-shows the loading state instead
    // of a dropdown holding stale data.
    this.dropdownItemsLoaded.next(false);
    try {
      let result = (await lastValueFrom(
        this.itemService.apiItemAllItemsGet({})
      )) as any;
      this.dropdownItemsList.next(
        Array.isArray(result) ? result : result?.data ? result.data : []
      );
    } catch (e) {
      this.dropdownItemsList.next([]);
      throw e;
    } finally {
      this.dropdownItemsLoaded.next(true);
    }
  }

  async getAllIUsersList(): Promise<void> {
    try {
      let result = this.userService.apiUserGet({ monitorOnly: "true" });
      let res = (await lastValueFrom(result)) as any;
      this.allUsersList.next(res.data ? res.data : []);
    } catch (error) {
      this.allUsersList.next([]);
      throw error;
    }
  }
}
